import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import { ConsentsTab } from './components/consent/ConsentsTab';
import { Contacts } from './components/consent/Contacts';
import { PartnerDetails } from './components/partner/PartnerDetails';
import { ProceedToDashboardButton } from './components/ProceedToDashboardButton';
import { ButtonContainer, Content, Logo, LogoutButtonContainer } from './InterstitialModal.styled';
import kindbody from '../../assets/logos/kindbody.svg';
import Checkmark from 'assets/icons/checkmark-green.svg?react';
import { Button } from 'components/v2/Buttons/Button/Button';
import { DeprecatedTabs } from 'components/v2/DeprecatedTabs/DeprecatedTabs';
import { TabIconPosition } from 'components/v2/DeprecatedTabs/DeprecatedTabs.types';
import { Modal } from 'components/v2/Modal/Modal';
import useHomeMarket from 'hooks/useHomeMarket';
import { useInterstitialStatus } from 'hooks/useInterstitialStatus';
import { pageUrl } from 'utilities/pageUrl';
import { useBreakpoints } from 'utilities/useBreakpoints';

import { Status } from './InterstitialModal.types';
import { activateModal, checkShouldRedirectBackToMembership } from './InterstitialModal.utils';

export const InterstitialModal = ({ open }: { open: boolean }) => {
  const { isMaxTablet } = useBreakpoints();

  const location = useLocation();
  const navigate = useNavigate();

  const { id } = useHomeMarket();
  const { status, loading: statusLoading } = useInterstitialStatus();
  const skipPartnerDataCapture = status?.skipPartnerDataCapture;
  const hasRequiredConsents = status?.hasRequiredConsents;
  const partnerDataCollected = status?.partnerDataCollected;

  useEffect(() => {
    if (statusLoading || !status) return;

    const shouldCollectPartnerData = !skipPartnerDataCapture && !partnerDataCollected;

    if (!hasRequiredConsents || shouldCollectPartnerData) {
      activateModal();
    }
  }, [skipPartnerDataCapture, hasRequiredConsents, partnerDataCollected, statusLoading, status]);

  if (statusLoading) return null;
  if (!id || !status?.active) return null;

  checkShouldRedirectBackToMembership(location.pathname, location.search, open);

  const options = getTabOptions({
    status,
    skipPartnerDataCapture
  });

  const firstIncompleteTab = options.find(option => !option.completed);

  return (
    <Modal
      title="Finish your account setup"
      open={open}
      onCancel={() => navigate(pageUrl.logout())}
      closable={false}
      maxWidth={900}
    >
      <Content>
        {isMaxTablet && <Logo src={kindbody} alt="kindbody" />}
        <Contacts />
        <DeprecatedTabs
          name="List of tabs for interstitial modal"
          options={options}
          size="lg"
          initialActiveTabId={firstIncompleteTab?.id}
        />
        <ButtonContainer>
          <ProceedToDashboardButton status={status} />
          <LogoutButtonContainer>
            <Button
              label={'Log Out'}
              category="secondary"
              fullWidth={isMaxTablet ?? 'false'}
              size="lg"
              onClick={() => {
                navigate(pageUrl.logout());
              }}
            />
          </LogoutButtonContainer>
        </ButtonContainer>
      </Content>
    </Modal>
  );
};

const getTabOptions = ({
  status,
  skipPartnerDataCapture
}: {
  status: Status;
  skipPartnerDataCapture: boolean | undefined;
}) => {
  const iconOption = {
    component: <Checkmark />,
    position: 'left' as TabIconPosition
  };

  const options = [
    {
      id: '1',
      label: 'Consents',
      panel: <ConsentsTab status={status} />,
      icon: status.hasRequiredConsents ? iconOption : undefined,
      completed: status.hasRequiredConsents
    }
  ];

  if (!skipPartnerDataCapture) {
    options.push({
      id: '2',
      label: 'Partner Details (If Applicable)',
      panel: <PartnerDetails status={status} />,
      icon: status.partnerDataCollected ? iconOption : undefined,
      completed: status.partnerDataCollected
    });
  }

  return options;
};

export default InterstitialModal;
